import { useEffect, useMemo, useState } from "react";
import {
  COMPONENT_META,
  COMPONENT_NAMES,
  COMPONENT_SCHEMAS,
  assignIds,
  parseNote,
  propsTable,
  rootProp,
  variantExample,
  type ComponentMeta,
  type EnumValue,
  type PropRow,
} from "@minne/core";
import { cn } from "@/lib/utils";
import { BlockRenderer } from "@/components/blocks/BlockRenderer";
import { CopyButton } from "@/components/ui/copy-button";
import { PageRail, columnClass, frameClass } from "@/components/OutlineRail";

const METAS = COMPONENT_NAMES.map((n) => COMPONENT_META[n]);
const RAIL_ITEMS = METAS.map((m) => ({ id: `comp-${m.name}`, text: m.title }));

/** Inline Markdown in a plain string: only code spans, no raw HTML. */
function Md({ text }: { text: string }) {
  const parts = text.split(/`([^`]+)`/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <code key={i} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground">
            {part}
          </code>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * Component reference: every component rendered by the real renderer, with its source,
 * its props table generated from the zod schema (in @minne/core) and a variant strip
 * for enum props. Nothing here is hand-written per component, so it cannot drift.
 */
export default function ComponentsDocsPage() {
  const [selected, setSelected] = useState<string>(METAS[0].name);

  // Keep the mobile select in sync with the section in view, so picking the entry it
  // already shows is never a dead tap.
  useEffect(() => {
    const sections = METAS.map((m) => document.getElementById(`comp-${m.name}`)).filter((el): el is HTMLElement => el !== null);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        if (top) setSelected(top.target.id.replace(/^comp-/, ""));
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 },
    );
    for (const el of sections) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={frameClass(true, "left")}>
      <div className={columnClass(true)}>
        <header className="pt-2">
          <h1 className="text-[1.75rem] font-semibold tracking-tight">Components</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Everything a note can contain, rendered exactly as in a note. Copy any example into a note or send it to a
            model; the props tables come from the same schemas the server validates against.
          </p>
        </header>

        {/* Below the width where the rail shows, a picker stands in for it. */}
        <div className="sticky top-[calc(3.25rem+env(safe-area-inset-top))] z-10 -mx-4 mt-4 border-b bg-background/90 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6 md:top-[env(safe-area-inset-top)] @min-[60rem]/main:hidden">
          <select
            aria-label="Component"
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              document.getElementById(`comp-${e.target.value}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="h-9 w-full rounded-md border bg-card px-2 text-sm"
          >
            {METAS.map((m) => (
              <option key={m.name} value={m.name}>
                {m.title}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-14 pt-10">
          {METAS.map((m) => (
            <ComponentSection key={m.name} meta={m} />
          ))}
        </div>
      </div>

      <PageRail side="left" title="Components" items={RAIL_ITEMS} active={`comp-${selected}`} />
    </div>
  );
}

function ComponentSection({ meta }: { meta: ComponentMeta }) {
  const examples = useMemo(() => meta.examples.map((source) => parseNote(assignIds(source).markdown)), [meta]);
  const rows = useMemo(() => {
    const derived = meta.derivedProps ?? [];
    return propsTable(COMPONENT_SCHEMAS[meta.name]).filter((r) => !derived.includes(rootProp(r.name)));
  }, [meta]);
  const variants = useMemo(() => {
    const source = meta.examples[0]!;
    return rows
      .filter((r) => r.depth === 0 && r.enum && r.enum.length > 1)
      .map((row) => ({
        row,
        items: row
          .enum!.map((value) => ({ value, parsed: variantExample(source, row.name, value) }))
          .filter((v): v is { value: EnumValue; parsed: string } => v.parsed !== null),
      }))
      .filter((strip) => strip.items.length > 1);
  }, [meta, rows]);

  return (
    <section id={`comp-${meta.name}`} className="scroll-mt-[calc(7.5rem+env(safe-area-inset-top))] md:scroll-mt-24">
      <h2 className="text-xl font-semibold tracking-tight">{meta.title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        <Md text={meta.summary} />. <Md text={meta.bodyNote} />
        {meta.hint ? (
          <>
            {" "}
            <Md text={meta.hint} />
          </>
        ) : null}
      </p>

      {examples.map((parsed, i) => (
        <Example key={i} source={meta.examples[i]!} blocks={parsed.blocks} />
      ))}

      {variants.map(({ row, items }) => (
        <div key={row.name} className="mt-6">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <code className="normal-case">{row.name}</code> variants
          </p>
          <div className="mt-2 flex flex-wrap items-start gap-4">
            {items.map(({ value, parsed }) => (
              <Variant key={String(value)} label={String(value)} source={parsed} />
            ))}
          </div>
        </div>
      ))}

      {rows.length > 0 && <PropsTable rows={rows} />}
    </section>
  );
}

function Example({ source, blocks }: { source: string; blocks: ReturnType<typeof parseNote>["blocks"] }) {
  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="rounded-xl border bg-card p-4">
        <BlockRenderer blocks={blocks} />
      </div>
      <div className="relative">
        <pre className="overflow-x-auto rounded-xl border bg-muted/50 p-3 pt-11 pr-11 font-mono text-xs leading-relaxed text-foreground/90">
          <code>{source}</code>
        </pre>
        <CopyButton
          text={source}
          label="Copy Markdown source"
          variant="secondary"
          className="absolute right-2 top-2"
        />
      </div>
    </div>
  );
}

function Variant({ label, source }: { label: string; source: string }) {
  const parsed = useMemo(() => parseNote(assignIds(source).markdown), [source]);
  return (
    <div className="min-w-48 flex-1 basis-56">
      <p className="mb-1.5 font-mono text-[11px] text-muted-foreground">{label}</p>
      <div className="rounded-xl border bg-card p-3">
        <BlockRenderer blocks={parsed.blocks} />
      </div>
    </div>
  );
}

function PropsTable({ rows }: { rows: PropRow[] }) {
  return (
    <div className="mt-6 overflow-x-auto rounded-xl border">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-semibold">Prop</th>
            <th className="px-3 py-2 font-semibold">Type</th>
            <th className="px-3 py-2 font-semibold">Required</th>
            <th className="px-3 py-2 font-semibold">Default</th>
            <th className="px-3 py-2 font-semibold">Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-border/60 last:border-0 align-top">
              <td className={cn("px-3 py-2 font-mono text-xs", r.depth > 0 && "pl-8 text-muted-foreground")}>
                {r.depth > 0 ? r.name.split(/[.[]/).pop() : r.name}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.type}</td>
              <td className="px-3 py-2">{r.required ? <span className="text-foreground">yes</span> : <span className="text-muted-foreground">no</span>}</td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.default ?? "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.description ? <Md text={r.description} /> : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
